import { format, isSameDay, subDays } from "date-fns";

import type { Encounter } from "@elation/api-client/src/stitch-web/models/encounter";
import type { EncounterListItem } from "@elation/api-client/src/stitch-web/models/encounterListItem";
import { EncounterStatus } from "@elation/api-client/src/stitch-web/models/encounterStatus";
import type { PaginatedEncounterListItem } from "@elation/api-client/src/stitch-web/models/paginatedEncounterListItem";

import { parseNullableDate } from "@src/shared/utils/dates";

type EncounterStatusValue = Encounter["status"];
type MaybeEncounter = Encounter | null | undefined;
type MaybeEncounterListItem = EncounterListItem | null | undefined;
type RecordingStep = string | null | undefined;

export type EncounterStage = "agenda" | "visit" | "note";
export type EncounterRecencyGroupId = "today" | "yesterday" | "earlier";

export interface EncounterRecencyGroup {
  id: EncounterRecencyGroupId;
  label: string;
  items: PaginatedEncounterListItem[];
}

interface EncounterStageInput {
  encounter: MaybeEncounter;
  recordingStep: RecordingStep;
  isEncounterFinalizing: boolean;
}

interface EncounterGeneratingNoteInput {
  encounter: MaybeEncounter;
  isEncounterFinalizing: boolean;
  recordingIsFinishing?: boolean;
  /**
   * `completedAt` captured when finish was requested. When the encounter actor
   * is generating a note, only a note whose completion version differs from
   * this baseline clears the generating surface.
   */
  completionBaseline?: string | null;
}

interface EncounterNoteReadyInput {
  encounter: MaybeEncounter;
  isGeneratingNote: boolean;
  completionBaseline?: string | null;
}

const VALID_TRANSITIONS: Record<
  EncounterStatusValue,
  readonly EncounterStatusValue[]
> = {
  [EncounterStatus.created]: [
    EncounterStatus.in_progress,
    EncounterStatus.finalizing,
    EncounterStatus.completed,
    EncounterStatus.closed,
  ],
  [EncounterStatus.in_progress]: [
    EncounterStatus.finalizing,
    EncounterStatus.completed,
    EncounterStatus.closed,
  ],
  [EncounterStatus.finalizing]: [
    EncounterStatus.in_progress,
    EncounterStatus.completed,
    EncounterStatus.closed,
  ],
  [EncounterStatus.completed]: [EncounterStatus.in_progress],
  [EncounterStatus.closed]: [EncounterStatus.in_progress],
};

const GROUPS: Array<Pick<EncounterRecencyGroup, "id" | "label">> = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "earlier", label: "Earlier" },
];

/**
 * Flatten SOAP/markdown note text into a single-line list preview.
 * Strips heading markers, list markers, rules, and simple emphasis so Past
 * Visits / visit lists show readable plain text rather than raw markdown.
 */
function normalizePreviewText(value: string): string {
  const flattened = value
    .split(/\r?\n/u)
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/u, "")
        .replace(/^\s*[-*+]\s+/u, "")
        .replace(/^\s*\d+\.\s+/u, "")
        .replace(/^\s*>\s?/u, "")
        .replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/u, "")
        .replace(/\*\*([^*]+)\*\*/gu, "$1")
        .replace(/__([^_]+)__/gu, "$1")
        .trim(),
    )
    .filter((line) => line.length > 0)
    .join(" ");

  return flattened.replace(/\s+/gu, " ").trim();
}

function compactParts(parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
}

function groupIdForDate(date: Date | null, now: Date): EncounterRecencyGroupId {
  if (!date) return "earlier";
  if (isSameDay(date, now)) return "today";
  if (isSameDay(date, subDays(now, 1))) return "yesterday";
  return "earlier";
}

function shortTime(date: Date): string {
  const suffix = format(date, "a").toLowerCase().startsWith("a") ? "a" : "p";
  return `${format(date, "h:mm")}${suffix}`;
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export const EncounterEntity = {
  canTransitionTo(
    encounter: MaybeEncounter,
    next: EncounterStatusValue,
  ): boolean {
    if (!encounter) return false;
    return VALID_TRANSITIONS[encounter.status].includes(next);
  },

  canComplete(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.created ||
      encounter.status === EncounterStatus.in_progress ||
      encounter.status === EncounterStatus.finalizing
    );
  },

  canReopen(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.completed ||
      encounter.status === EncounterStatus.closed
    );
  },

  isActive(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.created ||
      encounter.status === EncounterStatus.in_progress
    );
  },

  /**
   * Whether the visit has left prep (`created`) and entered recording.
   * Prefer `startedAt`; fall back to `in_progress` status.
   */
  isStarted(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.startedAt != null ||
      encounter.status === EncounterStatus.in_progress
    );
  },

  /**
   * Primary patient-chrome CTA for today's active encounter.
   */
  patientChromeVisitAction(
    encounter: MaybeEncounter,
  ): "start" | "resume" | null {
    if (!encounter) return null;
    if (EncounterEntity.isStarted(encounter)) return "resume";
    if (encounter.status === EncounterStatus.created) return "start";
    return null;
  },

  isFinalizing(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return encounter.status === EncounterStatus.finalizing;
  },

  isCompleted(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return (
      encounter.status === EncounterStatus.completed ||
      encounter.status === EncounterStatus.closed
    );
  },

  isGeneratingNote(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    if (EncounterEntity.hasGeneratedNote(encounter)) return false;

    return EncounterEntity.isFinalizing(encounter);
  },

  /**
   * Whether the fetched encounter is still waiting on a generated note —
   * server finalizing, or completed before note persistence lands.
   * Closed is terminal without a pending note (abandoned / failed /
   * superseded) and must not keep the client polling for generation.
   */
  isAwaitingGeneratedNote(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    if (EncounterEntity.hasGeneratedNote(encounter)) return false;

    return (
      EncounterEntity.isFinalizing(encounter) ||
      encounter.status === EncounterStatus.completed
    );
  },

  /**
   * Whether the visit should show the note-generation surface.
   *
   * Sources, in priority order:
   * - caller-supplied note-generation lifecycle (`isEncounterFinalizing`),
   *   cleared only by a finish-versioned generated note when a baseline is known
   * - fetched encounter awaiting a note (finalizing / completed+null)
   * - local scribe finishing (not sticky finished — that survives remount
   *   forever and would trap the UI until refresh)
   */
  shouldShowGeneratingNote({
    encounter,
    isEncounterFinalizing,
    recordingIsFinishing = false,
    completionBaseline,
  }: EncounterGeneratingNoteInput): boolean {
    if (isEncounterFinalizing) {
      return !EncounterEntity.hasGeneratedNoteForFinish(
        encounter,
        completionBaseline ?? null,
      );
    }
    if (EncounterEntity.isAwaitingGeneratedNote(encounter)) return true;
    if (recordingIsFinishing) {
      return !EncounterEntity.hasGeneratedNote(encounter);
    }
    return false;
  },

  stage({
    encounter,
    recordingStep,
    isEncounterFinalizing,
  }: EncounterStageInput): EncounterStage {
    if (
      isEncounterFinalizing ||
      EncounterEntity.isAwaitingGeneratedNote(encounter) ||
      (EncounterEntity.isCompleted(encounter) &&
        EncounterEntity.hasGeneratedNote(encounter))
    ) {
      return "note";
    }

    if (
      EncounterEntity.isGeneratingNote(encounter) ||
      recordingStep !== "initial"
    ) {
      return "visit";
    }

    return "agenda";
  },

  hasGeneratedNote(encounter: MaybeEncounter): boolean {
    if (!encounter) return false;
    return encounter.note != null;
  },

  /**
   * Whether the encounter has a generated note for the current finish attempt.
   *
   * Closed encounters never count — a historical note must not clear generation.
   *
   * Null baseline (first finish): any non-null note while `finalizing` or
   * `completed` clears generation. Note prediction often writes the SOAP note
   * before status/`completedAt` land; requiring those trapped the generating UI
   * until a full page refresh.
   *
   * Non-null baseline (reopen finish): require `completed` plus a `completedAt`
   * that differs from the baseline so a preserved historical note cannot
   * satisfy note-ready.
   */
  hasGeneratedNoteForFinish(
    encounter: MaybeEncounter,
    completionBaseline: string | null,
  ): boolean {
    if (!encounter) return false;
    if (!EncounterEntity.hasGeneratedNote(encounter)) return false;
    if (encounter.status === EncounterStatus.closed) return false;

    if (completionBaseline == null) {
      return (
        encounter.status === EncounterStatus.completed ||
        encounter.status === EncounterStatus.finalizing
      );
    }

    if (encounter.status !== EncounterStatus.completed) return false;
    const completedAt = encounter.completedAt ?? null;
    if (completedAt == null) return false;
    return completedAt !== completionBaseline;
  },

  /**
   * Whether the visit should render the completed-note surface.
   *
   * While generating, only a finish-versioned note counts (see
   * `hasGeneratedNoteForFinish`); otherwise a note only counts once the
   * encounter is completed/closed, so an in-progress historical note can't
   * jump straight to the completed view.
   */
  isNoteReadyForDisplay({
    encounter,
    isGeneratingNote,
    completionBaseline = null,
  }: EncounterNoteReadyInput): boolean {
    if (isGeneratingNote) {
      return EncounterEntity.hasGeneratedNoteForFinish(
        encounter,
        completionBaseline,
      );
    }
    return (
      EncounterEntity.isCompleted(encounter) &&
      EncounterEntity.hasGeneratedNote(encounter)
    );
  },

  hasNoteText(encounter: MaybeEncounter): boolean {
    return EncounterEntity.notePreview(encounter) != null;
  },

  notePreview(
    encounter: MaybeEncounter | MaybeEncounterListItem,
    maxLength = 160,
  ): string | null {
    if (!encounter) return null;
    const value =
      "notePreview" in encounter ? encounter.notePreview : encounter.note;
    if (value == null) return null;

    const normalized = normalizePreviewText(value);
    if (!normalized) return null;
    if (normalized.length <= maxLength) return normalized;

    return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
  },

  startedAt(encounter: MaybeEncounter): Date | null {
    return parseNullableDate(encounter?.startedAt);
  },

  finalizingAt(encounter: MaybeEncounter): Date | null {
    return parseNullableDate(encounter?.finalizingAt);
  },

  completedAt(encounter: MaybeEncounter): Date | null {
    return parseNullableDate(encounter?.completedAt);
  },

  lastLifecycleDate(encounter: MaybeEncounter): Date | null {
    return (
      EncounterEntity.completedAt(encounter) ??
      EncounterEntity.finalizingAt(encounter) ??
      EncounterEntity.startedAt(encounter)
    );
  },

  completedOrStartedAt(item: MaybeEncounterListItem): Date | null {
    return parseNullableDate(item?.completedAt ?? item?.startedAt);
  },

  visitDateLabel(item: MaybeEncounterListItem): string | null {
    const date = EncounterEntity.completedOrStartedAt(item);
    if (!date) return null;

    return new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  },

  /** Ed visit crumb: `6/17/2026 · 9:15a`. */
  visitDateTimeLabel(encounter: MaybeEncounter): string | null {
    const date = parseNullableDate(
      encounter?.completedAt ?? encounter?.startedAt,
    );
    if (!date) return null;

    return `${format(date, "M/d/yyyy")} · ${shortTime(date)}`;
  },

  physicianDisplayName(item: MaybeEncounterListItem): string | null {
    if (!item) return null;

    return (
      compactParts([item.physicianFirstName, item.physicianLastName]) || null
    );
  },

  patientVisitMetadata(item: MaybeEncounterListItem): string | null {
    const metadata = [
      EncounterEntity.visitDateLabel(item),
      EncounterEntity.physicianDisplayName(item),
    ]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(" · ");

    return metadata || null;
  },

  patientDisplayName(item: PaginatedEncounterListItem): string {
    return compactParts([
      item.patient.firstName,
      item.patient.middleName,
      item.patient.lastName,
    ]);
  },

  patientInitials(item: PaginatedEncounterListItem): string {
    const first = item.patient.firstName.trim().charAt(0);
    const last = item.patient.lastName.trim().charAt(0);
    const initials = `${first}${last}`.toUpperCase();

    return initials || "?";
  },

  startedAtDate(item: PaginatedEncounterListItem): Date | null {
    return parseNullableDate(item.startedAt);
  },

  startedAtRecencyLabel(
    item: PaginatedEncounterListItem,
    groupId: EncounterRecencyGroupId,
  ): string {
    const date = EncounterEntity.startedAtDate(item);
    if (!date) return "Time unavailable";

    if (groupId === "today") return `Today · ${shortTime(date)}`;
    if (groupId === "yesterday") return `Yesterday · ${shortTime(date)}`;
    return `${format(date, "EEE")} · ${shortTime(date)}`;
  },

  listMetadata(
    item: PaginatedEncounterListItem,
    groupId: EncounterRecencyGroupId,
  ): string {
    return [
      EncounterEntity.startedAtRecencyLabel(item, groupId),
      pluralize(item.agendaItemCount, "item"),
      pluralize(item.codeCount, "code"),
    ].join(" · ");
  },

  groupByRecency(
    items: PaginatedEncounterListItem[],
    now: Date = new Date(),
  ): EncounterRecencyGroup[] {
    const buckets: Record<
      EncounterRecencyGroupId,
      PaginatedEncounterListItem[]
    > = {
      today: [],
      yesterday: [],
      earlier: [],
    };

    for (const item of items) {
      buckets[groupIdForDate(EncounterEntity.startedAtDate(item), now)].push(
        item,
      );
    }

    return GROUPS.flatMap((group) => {
      const groupItems = buckets[group.id];
      return groupItems.length ? [{ ...group, items: groupItems }] : [];
    });
  },
} as const;
